import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, AlertTriangle, FileWarning, ExternalLink, Loader2 } from "lucide-react";
import { adminApi } from "../../lib/api";
import { money } from "../../lib/jobCalc";

const COLORS = ["#10b981", "#f59e0b", "#f28a16", "#dc2626"];
const ICONS = [Wallet, FileWarning, AlertTriangle, AlertTriangle];

export const AdminAging = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.aging();
        setData(r);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="text-[#3f4b52] inline-flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading…</div>;
  if (!data) return null;

  return (
    <div data-testid="admin-aging-page">
      <h1 className="font-display font-black uppercase text-3xl sm:text-4xl text-[#111619]">Outstanding</h1>
      <p className="text-[#3f4b52] mt-1">
        Aging report for unpaid invoices. As at {new Date(data.as_of).toLocaleString("en-ZA")}.
      </p>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {data.buckets.map((b, i) => {
          const Icon = ICONS[i];
          return (
            <div key={b.label} className="bg-white border border-[#cfd7db] rounded-md p-5 shadow-sm" data-testid={`aging-bucket-${i}`}>
              <div className="flex items-center justify-between">
                <div className="font-display uppercase tracking-widest text-[11px] text-[#3f4b52]">{b.label}</div>
                <div className="h-9 w-9 rounded-md flex items-center justify-center" style={{ background: `${COLORS[i]}1a`, color: COLORS[i] }}>
                  <Icon size={18} />
                </div>
              </div>
              <div className="mt-2 font-display font-black text-2xl text-[#111619]">{money(b.total)}</div>
              <div className="text-xs text-[#3f4b52] mt-1">{b.jobs.length} invoice(s)</div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 bg-[#20282c] text-white rounded-md p-5 flex items-center justify-between">
        <div>
          <div className="font-display uppercase tracking-widest text-xs text-[#ffc438]">Total outstanding</div>
          <div className="font-display font-black text-3xl">{money(data.grand_total)}</div>
        </div>
        <div className="text-sm">{data.job_count} unpaid job(s)</div>
      </div>

      <div className="mt-8 space-y-6">
        {data.buckets.map((b, i) => b.jobs.length > 0 && (
          <section key={b.label} className="bg-white border border-[#cfd7db] rounded-md overflow-hidden shadow-sm">
            <div className="px-4 py-2.5 font-display uppercase tracking-widest text-xs flex items-center gap-2"
                 style={{ background: `${COLORS[i]}1a`, color: COLORS[i] }}>
              {b.label} · {b.jobs.length} · {money(b.total)}
            </div>
            <table className="w-full text-sm">
              <thead className="bg-[#edf2f4] text-left">
                <tr className="font-display uppercase tracking-widest text-xs text-[#3f4b52]">
                  <th className="px-4 py-2">Job</th>
                  <th className="px-4 py-2">Client</th>
                  <th className="px-4 py-2">Vehicle</th>
                  <th className="px-4 py-2">Insurer</th>
                  <th className="px-4 py-2 text-right">Age</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                  <th className="px-4 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#eef2f4]">
                {b.jobs.map((j) => (
                  <tr key={j.id} data-testid={`aging-row-${j.id}`}>
                    <td className="px-4 py-2.5">
                      <Link to={`/admin/jobs/${j.id}`} className="font-display font-bold text-[#111619] hover:text-[#f28a16]">
                        #{j.job_number}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5">{j.client_name}</td>
                    <td className="px-4 py-2.5 text-[#3f4b52]">{j.vehicle || "—"} · {j.registration || "—"}</td>
                    <td className="px-4 py-2.5 text-[#3f4b52]">
                      {j.payment_type === "cash" ? "Cash" : (j.insurance_company || "—")}
                    </td>
                    <td className="px-4 py-2.5 text-right font-semibold" style={{ color: COLORS[i] }}>{j.age_days}d</td>
                    <td className="px-4 py-2.5 text-right font-semibold">{money(j.total_inc_vat)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <Link
                        to={`/admin/jobs/${j.id}`}
                        className="inline-flex items-center gap-1 px-2 h-8 rounded-md bg-[#20282c] text-white text-xs font-display uppercase tracking-wider hover:brightness-110"
                        data-testid={`aging-followup-${j.id}`}
                      >
                        Follow up <ExternalLink size={11} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ))}
      </div>
    </div>
  );
};

export default AdminAging;
